import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by
 * `<theme-drawer>`.
 *
 * This component handles:
 * - auto-opening after an item is added
 * - sticky cart summary layout
 * - closing for the installments CTA
 * - closing when navigating to another internal page
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    document.addEventListener(
      'click',
      this.#handleInternalNavigation,
      true
    );

    window.addEventListener('popstate', this.#closeDrawer);
    window.addEventListener('pageshow', this.#handlePageShow);

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Vessel may restore the drawer's open state after navigation.
     * Close that restored state so each new page opens cleanly.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      requestAnimationFrame(() => {
        this.#themeDrawer?.close();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    document.removeEventListener(
      'click',
      this.#handleInternalNavigation,
      true
    );

    window.removeEventListener('popstate', this.#closeDrawer);
    window.removeEventListener('pageshow', this.#handlePageShow);

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Close the drawer through ThemeDrawer's own lifecycle.
   * This also clears its backdrop, scroll lock and open state.
   */
  #closeDrawer = () => {
    if (this.#themeDrawer?.isOpen || this.#themeDrawer?.hasAttribute('open')) {
      this.#themeDrawer.close();
    }
  };

  /**
   * Close the drawer when a cached page is restored.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (event.persisted || this.#themeDrawer?.hasAttribute('open')) {
      this.#closeDrawer();
    }
  };

  /**
   * Close the drawer before navigating to another internal page.
   *
   * @param {MouseEvent} event
   */
  #handleInternalNavigation = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target =
      event.target instanceof Element
        ? event.target.closest('a[href]')
        : null;

    if (!(target instanceof HTMLAnchorElement)) return;
    if (target.target === '_blank') return;
    if (target.hasAttribute('download')) return;

    let destination;

    try {
      destination = new URL(target.href, window.location.href);
    } catch {
      return;
    }

    if (destination.origin !== window.location.origin) return;

    const currentUrl = new URL(window.location.href);

    const isSamePage =
      destination.pathname === currentUrl.pathname &&
      destination.search === currentUrl.search;

    /*
     * Do not close for same-page anchor links.
     */
    if (isSamePage) return;

    this.#closeDrawer();
  };

  /**
   * Handles the theme-drawer opening.
   * Updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}